import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import BrainIcon from "../../../assets/icons/brain-duotone.svg?react";
import DiplomaIcon from "../../../assets/icons/diploma-duotone.svg?react";
import TubeIcon from "../../../assets/icons/tube-duotone.svg?react";
import { displayToast } from "../../../components/app/AppToast";
import { NavShare } from "../../../components/app/headers/layout";
import ContentHeader from "../../../components/app/headers/ContentHeader";
import CustomIcon from "../../../components/CustomIcon";
import GamificationBadge from "../../../components/gamification/GamificationBadge";
import Loader from "../../../components/Loader";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../../domains/user/hooks/useCurrentUser";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import { renderDate } from "../../../domains/app/utils/renderDate";
import { shareUrl } from "../../../domains/app/utils/shareUrl";
import { apiClient, type Api } from "../../../services/api";
import ScrollableLayout from "../../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";

const ESTIMATED_ARTICLE_READ_TIME = 31.03;

export default function ProfileGamificationPage({
  isMe,
  id,
}: {
  isMe: boolean;
  id?: string;
}) {
  const history = useHistory();
  const userId = id ?? null;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<
    | Extract<
        Api["get"]["/webapp/private/users/:id"]["output"],
        { status: 200 }
      >["body"]
    | null
  >(null);
  const { currentUser } = useCurrentUser();

  useEffect(() => {
    window.scroll(0, 0);
    initialize();
  }, [history]);

  async function initialize() {
    try {
      await fetchProfile();
    } catch (error) {
      console.error("Error during initialization", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  async function fetchProfile() {
    try {
      const targetId = isMe ? currentUser?.id : userId;
      if (targetId === undefined || targetId === null) {
        throw new Error("No user ID provided");
      }

      if (isMe) {
        if (!navigator.onLine) {
          setIsLoading(false);
          return;
        }
        reloadCurrentUser();
      }

      const { body } = await apiClient.get("/webapp/private/users/:id", {
        param: { id: targetId },
      });
      setUser(body);
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      history.goBack();
    } finally {
      setIsLoading(false);
    }
  }

  const currentStreak = user?.metrics.currentStreak ?? 0;
  const longestStreak = user?.metrics.longestStreak ?? 0;

  const longestStreakEndDate = user?.metrics.longestStreakEndDate ?? null;
  const longestStreakStartDate =
    longestStreakEndDate === null
      ? null
      : new Date(
          new Date(longestStreakEndDate).getTime() -
            longestStreak * 24 * 60 * 60 * 1000
        );

  function renderNoticeText(currentStreak: number) {
    if (currentStreak === 0) {
      return t("Consume content today to kick-off your learning streak");
    }

    if (currentStreak > 90) {
      return t(
        "Congratulations, you have reached the maximum series limit. It's time to start again."
      );
    }

    const daysRemaining =
      currentStreak < 5 ? 5 - currentStreak : 5 - (currentStreak % 5);

    return `${daysRemaining} ${
      daysRemaining === 1 ? t("more day") : t("more days")
    } ${t("to unlock new achievements")}`;
  }

  function formatMinutesToTime(time: number) {
    const minsNum = time;
    const hours = Math.floor(minsNum / 60);
    const minutes = Math.floor(minsNum - (hours * 3600) / 60);

    const formattedHours = hours < 10 ? "0" + hours : hours;
    const formattedMinutes = minutes < 10 ? "0" + minutes : minutes;

    return (
      <div className="render-saved-time">
        {isNaN(Number(hours)) ? 0 : formattedHours}
        <span>{t("hours")}</span>{" "}
        {isNaN(Number(minutes)) ? 0 : formattedMinutes}
        <span>{t("minutes")}</span>
      </div>
    );
  }

  function getProfileMetrics() {
    if (!user?.metrics) return [];

    const articleViews = user.metrics.articleViews ?? 0;
    const articleShares = user.metrics.articleShares ?? 0;
    const contentViews = user.metrics.contentViews ?? 0;
    const contentShares = user.metrics.contentShares ?? 0;

    // TODO: Wait until backend implements this
    // const savedArticlesIds: string[] = [];
    // const savedContentIds: string[] = [];

    // const totalSavedArticles = savedArticlesIds.length;
    // const totalSavedContent = savedContentIds.length;

    return [
      {
        metric: t("Total Articles Read"),
        count: articleViews,
        icon: "metricTotalArticlesRead",
      },
      // NOTE: Wait until backend implements this
      // {
      //   metric: t("Total Articles Saved"),
      //   count: totalSavedArticles ?? 0,
      //   icon: "metricTotalArticlesSaved",
      // },
      {
        metric: t("Total Articles Shared"),
        count: articleShares,
        icon: "metricTotalArticlesShared",
      },
      {
        metric: t("Total Videos Watched"),
        count: contentViews,
        icon: "metricTotalVideosWatched",
      },
      // NOTE: Wait until backend implements this
      // {
      //   metric: t("Total Videos Saved"),
      //   count: totalSavedContent.length ?? 0,
      //   icon: "metricTotalVideosSaved",
      // },
      {
        metric: t("Total Videos Shared"),
        count: contentShares,
        icon: "metricTotalVideosShared",
      },
      {
        metric: t("of Total Reading Time Saved"),
        count: formatMinutesToTime(articleViews * ESTIMATED_ARTICLE_READ_TIME),
        icon: "metricTotalReadingTimeSaved",
      },
    ];
  }

  async function handleShare(user: { id: string }) {
    try {
      const text = "";
      const url = `${globalThis.location.origin}/user/game/activity/${user.id}`;
      const outcome = await shareUrl(
        document.title,
        text,
        Promise.resolve(url)
      );

      if (outcome === "sent") displayToast(t("Profile shared"), "success");
    } catch (error) {
      console.error(error);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <ContentHeader
          transparent
          actions={
            user === null ? undefined : (
              <NavShare onClick={() => handleShare(user)} />
            )
          }
        />
      }
    >
      {isLoading ? (
        <Loader expanded />
      ) : (
        <>
          <div className="flex flex-col gap-5 text-center font-dm-sans text-primary-black py-10 bg-[linear-gradient(360deg,#ffc408_0%,#ff8800_51.56%,#ff7fbd_100%)] [&_.name-text]:font-light [&_.name-text_b]:font-bold [&_.specialties-text]:font-dm-sans [&_.specialties-text]:text-[11px] [&_.current-streak-text]:font-medium [&_.current-streak-text]:text-xs [&_.current-streak-text]:text-white [&_.current-streak-text]:tracking-[0.06em] [&_.current-streak-text]:uppercase [&_.streak-numbers]:flex [&_.streak-numbers]:justify-between [&_.streak-numbers]:items-center [&_.streak-numbers]:my-0 [&_.streak-numbers]:mx-[-40px] [&_.streak-numbers]:overflow-hidden [&_.streak-numbers]:translate-x-[-5px] [&_.streak-numbers>span:nth-child(1)]:opacity-[0.24] [&_.streak-numbers>span:nth-child(3)]:opacity-[0.24] [&_.streak-numbers_span]:w-[120px] [&_.streak-numbers_span]:font-bold [&_.streak-numbers_span]:text-[100px] [&_.streak-numbers_span]:leading-[115%] [&_.streak-numbers_span]:text-white [&_.streak-days-text]:font-bold [&_.streak-days-text]:text-[31px] [&_.streak-days-text]:leading-[100%] [&_.streak-days-text]:tracking-widest [&_.streak-days-text]:uppercase [&_.streak-days-text]:text-white [&_.streak-text]:italic [&_.streak-text]:font-semibold [&_.streak-text]:text-[17px] [&_.streak-text]:opacity-70 [&_.streak-text]:text-white [&_.streak-text]:mx-[60px] [&_.notice-text]:font-bold [&_.notice-text]:text-xs [&_.notice-text]:text-center [&_.notice-text]:tracking-[0.02em] [&_.notice-text]:px-[40px] [&_.notice-text]:mt-[10px]">
            <div>
              <span className="name-text">
                {user?.firstname} <b>{user?.lastname}</b>
              </span>
              {user !== null &&
                user.professionModel !== null &&
                user.mainSpecialtyModel !== null && (
                  <div className="specialties-text">
                    #{getItemTranslation(user.professionModel)} #
                    {getItemTranslation(user.mainSpecialtyModel)}
                  </div>
                )}
            </div>
            <div>
              <span className="current-streak-text">{t("Current Streak")}</span>
              <div className="streak-numbers">
                <span>
                  {currentStreak - 1 > 0
                    ? `${currentStreak - 1}`.padStart(2, "0")
                    : ""}
                </span>
                <span>{`${currentStreak}`.padStart(2, "0")}</span>
                <span>
                  {currentStreak < 90
                    ? `${currentStreak + 1}`.padStart(2, "0")
                    : ""}
                </span>
              </div>
              <span className="streak-days-text">{t("days")}</span>
            </div>
            {isMe && (
              <>
                <span className="streak-text">
                  {t("A few minutes a day to stay up-to-date!")}
                </span>
                <span className="notice-text">
                  {renderNoticeText(currentStreak)}
                </span>
              </>
            )}
          </div>
          <div className="flex flex-col p-[40px_20px] gap-8">
            <div className="p-[24px_21px] bg-white shadow-[2px_2px_10px_rgba(33,33,33,0.1)] rounded-2xl relative flex justify-between items-center gap-[18px] [&_.streak-card-icon]:absolute [&_.streak-card-icon]:top-[-20px] [&_.streak-card-icon]:left-[15px] [&_.streak-card-title]:italic [&_.streak-card-title]:uppercase [&_.streak-card-title]:font-semibold [&_.streak-card-title]:text-[17px] [&_.streak-card-title]:text-primary-black [&_.date-range]:font-normal [&_.date-range]:text-[11px] [&_.date-range]:leading-[115%] [&_.date-range]:tracking-[0.04em] [&_.date-range]:text-secondary-bluegrey-5 [&_.streak-card-number]:font-bold [&_.streak-card-number]:text-[40px] [&_.streak-card-number]:text-center [&_.streak-card-number]:uppercase [&_.streak-card-number]:text-secondary-raspberry-1 [&_.streak-card-number]:flex [&_.streak-card-number]:flex-col [&_.streak-card-number_span]:text-sm">
              <DiplomaIcon
                height={45}
                width={45}
                className="streak-card-icon"
              />
              <div>
                <span className="streak-card-title">
                  {isMe ? t("your longest streak") : t("their longest streak")}
                </span>
                {longestStreakStartDate !== null && (
                  <div className="date-range">
                    {t("From")}{" "}
                    <time>
                      {renderDate(
                        new Date(longestStreakStartDate.toISOString()),
                        true
                      )}
                    </time>{" "}
                    {t("to")}{" "}
                    <time>
                      {renderDate(
                        new Date(user?.metrics.longestStreakEndDate || ""),
                        true
                      )}
                    </time>
                  </div>
                )}
              </div>
              <span className="streak-card-number">
                {`${longestStreak}`.padStart(2, "0")}
                <span>{t("days")}</span>
              </span>
            </div>
            <hr />
            <div className="flex items-center gap-4 font-dm-sans font-bold text-[21px] leading-[120%] text-primary-black">
              <TubeIcon />
              <span>{t("Statistics")}</span>
            </div>
            {getProfileMetrics().map((item, index) => (
              <div
                key={item.metric + index * (Number(item.count) || 0)}
                className="flex items-center gap-[15px] [&_.metric-text]:font-dm-sans [&_.metric-text]:font-bold [&_.metric-text]:text-[32px] [&_.metric-text]:text-primary-black [&_.metric-text_.subtitle]:block [&_.metric-text_.subtitle]:font-dm-sans [&_.metric-text_.subtitle]:text-[11px] [&_.metric-text_.render-saved-time]:font-dm-sans [&_.metric-text_.render-saved-time]:font-bold [&_.metric-text_.render-saved-time]:text-[32px] [&_.metric-text_.render-saved-time]:tracking-[0.04em] [&_.metric-text_.render-saved-time]:text-primary-black [&_.metric-text_.render-saved-time_span]:text-base"
              >
                <CustomIcon iconName={item.icon} />
                <div className="metric-text">
                  {item.count ?? 0}{" "}
                  <span className="subtitle">{item.metric}</span>
                </div>
              </div>
            ))}
            <hr />
            <div className="flex items-center gap-4 font-dm-sans font-bold text-[21px] leading-[120%] text-primary-black">
              <BrainIcon />
              <span>{t("Current Streak")}</span>
            </div>
            <div className="grid grid-cols-3 gap-3 justify-items-center">
              {Array.from({ length: 18 }, (_, i) => (i + 1) * 5).map(
                (count) => (
                  <GamificationBadge
                    key={count}
                    count={count}
                    disabled={currentStreak < count}
                  />
                )
              )}
            </div>
          </div>
        </>
      )}
    </ScrollableLayout>
  );
}
